import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useScenarios } from '../hooks/useScenarios';
import { ScenarioPreview } from '../components/ScenarioPreview';
import { Scenario } from '../types';
import { PermissionGate } from '../../admin/components/PermissionGate';
import { Permissions } from '../../admin/hooks/usePermissions';
import { SystemCheckModal } from '../../readiness';
import { showConfirm, showError, showToast } from '../../../utils/alerts';

import { AssignLearnersModal } from '../../admin/components/AssignLearnersModal';
import { TenantAvailabilityModal } from '../../admin/components/TenantAvailabilityModal';
import { usePermissions } from '../../admin/hooks/usePermissions';
import { useTranslation } from '../../../i18n';

export const ScenarioDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getScenario, publishScenario, unpublishScenario, duplicateScenario, deleteScenario } = useScenarios({ autoFetch: false });
  const { isPlatformAdmin, canAssign } = usePermissions();

  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreflightOpen, setIsPreflightOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);

  const loadData = () => {
    if (!id) return;
    setLoading(true);
    getScenario(id)
      .then((data) => {
        if (data) {
          setScenario(data);
        } else {
          setError('Scenario not found.');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const [pendingPublishAfterAssign, setPendingPublishAfterAssign] = useState(false);

  const handlePublish = async () => {
    if (!id) return;
    const assignedCount = (scenario as any)?.assignedCount || 0;
    if (assignedCount === 0 && scenario?.status !== 'PUBLISHED') {
      showToast('Please assign learners to this simulation scenario before publishing.', 'info');
      setPendingPublishAfterAssign(true);
      setIsAssignModalOpen(true);
      return;
    }

    setProcessing(true);
    try {
      const res = await publishScenario(id);
      if (res.success && res.data) {
        setScenario(res.data);
        showToast('Scenario published successfully!', 'success');
      } else {
        const errMsg = res.error || 'Failed to publish scenario.';
        if (errMsg.includes('assign') || errMsg.includes('learner')) {
          showToast('Please assign learners to this simulation scenario before publishing.', 'info');
          setPendingPublishAfterAssign(true);
          setIsAssignModalOpen(true);
        } else {
          showError('Publish Failed', errMsg);
        }
      }
    } finally {
      setProcessing(false);
    }
  };

  const handlePublishAndAssign = async () => {
    if (!id) return;
    setPendingPublishAfterAssign(true);
    setIsAssignModalOpen(true);
  };

  const handleUnpublish = async () => {
    if (!id) return;
    setProcessing(true);
    try {
      const res = await unpublishScenario(id);
      if (res.success && res.data) {
        setScenario(res.data);
        showToast('Scenario reverted to draft', 'info');
      } else {
        showError('Unpublish Failed', res.error || 'Failed to unpublish scenario.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleDuplicate = async () => {
    if (!id) return;
    setProcessing(true);
    try {
      const res = await duplicateScenario(id);
      if (res.success && res.data) {
        showToast(`Duplicated "${res.data.title}" successfully`, 'success');
        navigate(`/scenarios/${res.data.id}`);
      } else {
        showError('Duplication Failed', res.error || 'Failed to duplicate scenario.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleArchive = async () => {
    if (!id || !scenario) return;
    const confirmed = await showConfirm({
      title: 'Archive Scenario?',
      text: `Are you sure you want to archive "${scenario.title}"?`,
      confirmText: 'Archive Scenario',
      cancelText: 'Cancel',
      icon: 'warning',
      isDestructive: true,
    });
    if (!confirmed) return;

    setProcessing(true);
    try {
      const res = await deleteScenario(id);
      if (res.success) {
        showToast('Scenario archived successfully', 'success');
        navigate('/scenarios');
      } else {
        showError('Archive Failed', res.error || 'Failed to archive scenario.');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading-state">
          <div className="dashboard-spinner" />
          <p>{t('scenarios.loading_dossier', undefined, 'Loading scenario dossier…')}</p>
        </div>
      </div>
    );
  }

  if (!scenario || error) {
    return (
      <div className="admin-page">
        <div className="admin-alert admin-alert--error">{error || t('scenarios.not_found', undefined, 'Scenario not found')}</div>
        <button type="button" onClick={() => navigate('/scenarios')} className="admin-btn admin-btn--secondary" style={{ width: 'fit-content' }}>
          {t('scenarios.back_to_scenarios', undefined, '← Back to Scenarios')}
        </button>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header with breadcrumbs and action buttons */}
      <div className="admin-page__header scenario-details-header">
        <div className="scenario-details-header-info" style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div className="admin-breadcrumbs">
            <Link to="/admin">{t('breadcrumb.admin', undefined, 'Admin')}</Link>
            <span className="breadcrumb-separator">›</span>
            <Link to="/scenarios">{t('breadcrumb.scenarios', undefined, 'Scenarios')}</Link>
            <span className="breadcrumb-separator">›</span>
            <span className="current" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{scenario.title}</span>
          </div>
          <h1 className="admin-page__title" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{scenario.title}</h1>
          <div className="admin-meta-row" style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              {t('scenarios.category', undefined, 'Category:')} <strong>{scenario.category || 'General'}</strong> • {t('scenarios.difficulty', undefined, 'Difficulty:')} <strong>{scenario.difficulty}</strong>
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '6px',
                background: ((scenario as any).assignedCount || 0) > 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                color: ((scenario as any).assignedCount || 0) > 0 ? '#16a34a' : '#ca8a04',
                border: ((scenario as any).assignedCount || 0) > 0 ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(234, 179, 8, 0.3)',
              }}
            >
              👥 {((scenario as any).assignedCount || 0)} {t('scenarios.learners_assigned', undefined, 'Learners Assigned')}
            </span>
          </div>
        </div>

        <div className="scenario-details-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Assign Learners Action Button (Admin & Super Admin only) */}
          {canAssign && (
            <button
              type="button"
              onClick={() => setIsAssignModalOpen(true)}
              className="admin-btn admin-btn--secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'var(--blue-soft, #eff6ff)',
                color: 'var(--blue, #2563eb)',
                borderColor: '#bfdbfe',
                fontWeight: 600,
              }}
              title="Assign this simulation to learners (multi-select or Excel upload)"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="17" rx="2" />
                <path d="M9 3h6v3H9zM8.5 12l2.5 2.5 4.5-4.5" />
              </svg>
              {t('scenarios.assign_learners', undefined, 'Assign Learners')}
            </button>
          )}

          {/* Super Admin Tenant Whitelist Action Button */}
          {isPlatformAdmin && (
            <button
              type="button"
              onClick={() => setIsTenantModalOpen(true)}
              className="admin-btn admin-btn--secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'var(--vio-soft, #f5f3ff)',
                color: 'var(--vio, #7c3aed)',
                borderColor: '#ddd6fe',
                fontWeight: 600,
              }}
              title="Map organizations that can access this simulation"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M7 7h.01M17 7h.01M7 17h.01M17 17h.01M12 7v10M7 12h10" />
              </svg>
              {t('scenarios.organization_access', undefined, 'Organization Access')}
            </button>
          )}

          <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
            <button
              type="button"
              disabled={processing}
              onClick={handleDuplicate}
              className="admin-btn admin-btn--secondary"
              title="Create a copy of this scenario"
            >
              {t('scenarios.duplicate', undefined, 'Duplicate')}
            </button>
          </PermissionGate>

          <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
            <button
              type="button"
              onClick={() => navigate(`/scenarios/${id}/evaluation-studio`)}
              className="admin-btn admin-btn--secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <svg style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              {t('scenarios.llm_prompts', undefined, 'LLM Evaluation Prompts')}
            </button>
          </PermissionGate>

          <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
            <button
              type="button"
              onClick={() => navigate(`/scenarios/${id}/edit`)}
              className="admin-btn admin-btn--secondary"
            >
              {t('scenarios.edit_scenario', undefined, 'Edit Scenario')}
            </button>
          </PermissionGate>

          <PermissionGate permission={Permissions.SCENARIO_PUBLISH}>
            {scenario.status === 'PUBLISHED' ? (
              <button
                type="button"
                disabled={processing}
                onClick={handleUnpublish}
                className="admin-btn admin-btn--warning"
              >
                {t('scenarios.revert_draft', undefined, 'Revert to Draft')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={processing}
                  onClick={handlePublish}
                  className="admin-btn admin-btn--primary"
                >
                  {t('scenarios.publish_scenario', undefined, 'Publish Scenario')}
                </button>
                {canAssign && (
                  <button
                    type="button"
                    disabled={processing}
                    onClick={handlePublishAndAssign}
                    className="admin-btn admin-btn--primary"
                    style={{
                      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                      borderColor: '#4f46e5',
                      fontWeight: 600,
                    }}
                    title="Publish this simulation and immediately assign learners"
                  >
                    {t('scenarios.publish_and_assign', undefined, '🚀 Publish & Assign')}
                  </button>
                )}
              </>
            )}
          </PermissionGate>

          <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
            {scenario.status !== 'ARCHIVED' && (
              <button
                type="button"
                disabled={processing}
                onClick={handleArchive}
                className="admin-btn admin-btn--danger"
                title="Archive Scenario"
              >
                {t('scenarios.archive', undefined, 'Archive')}
              </button>
            )}
          </PermissionGate>

          {scenario.status === 'PUBLISHED' && (
            <button
              type="button"
              onClick={() => {
                navigate(`/simulations/${scenario.id}/briefing`);
              }}
              className="admin-btn admin-btn--primary scenario-start-sim-btn"
              style={{ background: 'var(--ok)', borderColor: 'var(--ok)' }}
            >
              <span>🚀</span> {t('scenarios.start_simulation', undefined, 'Start Simulation')}
            </button>
          )}
        </div>
      </div>

      {/* Assign Learners Modal (2.1 & 2.2) */}
      {canAssign && (
        <AssignLearnersModal
          simulationId={scenario.id}
          simulationTitle={scenario.title}
          tenantId={scenario.tenantId}
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setPendingPublishAfterAssign(false);
          }}
          onAssigned={async (count) => {
            setIsAssignModalOpen(false);
            if (pendingPublishAfterAssign && scenario.status !== 'PUBLISHED') {
              setPendingPublishAfterAssign(false);
              showToast(`Assigned to ${count} learners! Publishing scenario...`, 'info');
              const pubRes = await publishScenario(scenario.id);
              if (pubRes.success && pubRes.data) {
                setScenario(pubRes.data);
                showToast(`Assigned to ${count} learners & scenario published successfully!`, 'success');
              } else {
                loadData();
              }
            } else {
              showToast(`Assigned scenario to ${count} learners successfully!`, 'success');
              loadData();
            }
          }}
        />
      )}

      {/* Super Admin Tenant Whitelist Modal */}
      {isPlatformAdmin && (
        <TenantAvailabilityModal
          simulationId={scenario.id}
          simulationTitle={scenario.title}
          isOpen={isTenantModalOpen}
          onClose={() => setIsTenantModalOpen(false)}
          onUpdated={() => {
            showToast('Organization availability updated', 'success');
          }}
        />
      )}

      {/* System Pre-Flight Check Modal */}
      <SystemCheckModal
        isOpen={isPreflightOpen}
        scenarioTitle={scenario.title}
        requiresMicrophone={scenario.requiresMicrophone ?? scenario.hardwareRequirements?.requiresMicrophone ?? false}
        requiresCamera={scenario.requiresCamera ?? scenario.hardwareRequirements?.requiresCamera ?? false}
        requiresSpeaker={scenario.requiresSpeaker ?? scenario.hardwareRequirements?.requiresSpeaker ?? false}
        onCancel={() => setIsPreflightOpen(false)}
        onComplete={() => {
          setIsPreflightOpen(false);
          navigate(`/simulations/${scenario.id}/briefing`);
        }}
      />

      {/* Briefing Dossier Preview */}
      <ScenarioPreview scenario={scenario} />
    </div>
  );
};
